import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type RecordCommentsResult } from '@whinerp/client';
import { Button, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { formatDateTime } from '@whinerp/contracts/format';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';

/**
 * Internal discussion on a record (G-077).
 *
 * Its own panel, beside the timeline rather than inside it. An activity is
 * something that happened with the customer; this is colleagues deciding what
 * to do about it, and a reader has to be able to tell at a glance which they
 * are looking at - most of all on a record somebody is about to hand over.
 *
 * Open questions come first. The reason anybody opens this panel is the
 * question nobody has answered, and a month of settled ones above it is how a
 * comment stream stops being read.
 */

type Comment = RecordCommentsResult['comments'][number];

export function CommentsPanel({
  objectKey,
  recordId,
  recordLabel,
}: {
  objectKey: string;
  recordId: string;
  recordLabel: string;
}): JSX.Element {
  const t = useT();
  const format = useFormat();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyBody, setReplyBody] = useState('');

  const comments = useQuery<RecordCommentsResult, ApiError>({
    queryKey: ['record-comments', objectKey, recordId],
    queryFn: () => api.recordComments({ path: { objectKey, recordId } }),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['record-comments', objectKey, recordId] });

  const post = useMutation({
    mutationFn: (input: { body: string; parentId: string | null }) =>
      api.postRecordComment({ path: { objectKey, recordId }, body: { ...input, recordLabel } }),
    onSuccess: async () => {
      setBody('');
      setReplyBody('');
      setReplyTo(null);
      await refresh();
    },
  });

  const resolve = useMutation({
    mutationFn: (input: { commentId: string; resolved: boolean }) =>
      api.resolveRecordComment({
        path: { objectKey, recordId, commentId: input.commentId },
        body: { resolved: input.resolved },
      }),
    onSuccess: refresh,
  });

  return (
    <Panel data-region="comments">
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-0.5">
          <SectionLabel>{t('comments.title')}</SectionLabel>
          {/* Said once, here: a panel that looked like the timeline would be
              used like the timeline. */}
          <p className="text-meta text-ink-meta">{t('comments.body')}</p>
        </div>

        <div className="flex flex-col gap-1">
          <textarea
            data-comment-input
            aria-label={t('comments.write')}
            placeholder={t('comments.write')}
            rows={2}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            className="rounded-control border border-control bg-surface px-3 py-2 text-body text-ink"
          />
          <Button
            intent="secondary"
            size="small"
            data-comment-post
            disabled={post.isPending || body.trim() === ''}
            onClick={() => post.mutate({ body, parentId: null })}
          >
            {t('comments.post')}
          </Button>
          {post.error ? (
            <p data-comment-refusal className="text-meta text-danger-text">{errorText(post.error)}</p>
          ) : null}
        </div>

        {comments.isPending ? <Skeleton className="h-block-md w-full" /> : null}
        {comments.isError ? (
          <p role="alert" className="text-meta text-danger-text">{errorText(comments.error)}</p>
        ) : null}

        {comments.data && comments.data.comments.length === 0 ? (
          <p data-comments-empty className="text-meta text-ink-meta">{t('comments.none')}</p>
        ) : null}

        <ul className="flex flex-col gap-2">
          {(comments.data?.comments ?? []).map((comment: Comment) => (
            <li
              key={comment.commentId}
              data-comment={comment.commentId}
              data-comment-resolved={comment.resolvedAt !== null ? 'true' : undefined}
              className={
                comment.resolvedAt !== null
                  ? 'flex flex-col gap-1 rounded-card border border-rule bg-sunken px-3 py-2 opacity-70'
                  : 'flex flex-col gap-1 rounded-card border border-rule bg-surface px-3 py-2'
              }
            >
              <span className="text-body text-ink">{comment.body}</span>
              <span className="text-meta text-ink-meta">
                {comment.authorName ?? t('comments.somebody')} · {formatDateTime(comment.createdAt, format)}
                {/* Visible, because a request quietly rewritten after a
                    colleague acted on it is the thing this guards against. */}
                {comment.editedAt ? ` · ${t('comments.edited')}` : ''}
              </span>

              {comment.replies.map((reply) => (
                <div key={reply.commentId} data-comment-reply={reply.commentId} className="ms-4 flex flex-col border-s border-s-rule ps-2">
                  <span className="text-dense text-ink">{reply.body}</span>
                  <span className="text-meta text-ink-meta">
                    {reply.authorName ?? t('comments.somebody')} · {formatDateTime(reply.createdAt, format)}
                  </span>
                </div>
              ))}

              <div className="flex flex-wrap gap-2">
                <Button intent="ghost" size="small" data-comment-reply-open={comment.commentId} onClick={() => setReplyTo(comment.commentId)}>
                  {t('comments.reply')}
                </Button>
                <Button
                  intent="ghost"
                  size="small"
                  data-comment-resolve={comment.commentId}
                  onClick={() => resolve.mutate({ commentId: comment.commentId, resolved: comment.resolvedAt === null })}
                >
                  {t(comment.resolvedAt === null ? 'comments.resolve' : 'comments.reopen')}
                </Button>
              </div>

              {replyTo === comment.commentId ? (
                <div className="flex flex-col gap-1">
                  <textarea
                    data-comment-reply-input
                    aria-label={t('comments.reply')}
                    rows={2}
                    value={replyBody}
                    onChange={(event) => setReplyBody(event.target.value)}
                    className="rounded-control border border-control bg-surface px-3 py-2 text-body text-ink"
                  />
                  <Button
                    intent="secondary"
                    size="small"
                    data-comment-reply-post={comment.commentId}
                    disabled={post.isPending || replyBody.trim() === ''}
                    onClick={() => post.mutate({ body: replyBody, parentId: comment.commentId })}
                  >
                    {t('comments.post')}
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    </Panel>
  );
}
